import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingDown, AlertTriangle, Users, Award, Download } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';
import { fmtDate, fmtCurrency, statusColor } from '../utils/helpers';
import { PageHeader, StatusBadge } from '../components/Table';

function MiniBar({ value, max, color = 'bg-blue-500' }) {
  const pct = max > 0 ? Math.round(value / max * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 w-8 text-right">{value}</span>
    </div>
  );
}

function MonthChart({ trend }) {
  const max = Math.max(...trend.map(t => t.done), 1);
  return (
    <div className="flex items-end gap-1 h-16">
      {trend.map((t, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full bg-blue-500 rounded-t" style={{ height: `${Math.round(t.done / max * 48)}px`, minHeight: t.done > 0 ? '4px' : '0' }} />
          <span className="text-xs text-gray-400">{t.month.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

export default function ReportsPage() {
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.tab || 'productivity');
  const [productivity, setProductivity] = useState([]);
  const [misses, setMisses] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [clientReport, setClientReport] = useState(null);
  const [empPerf, setEmpPerf] = useState([]);
  const [empComparison, setEmpComparison] = useState([]);
  const [empFilterId] = useState(location.state?.empId || null);
  const [empFilter, setEmpFilter] = useState('');
  const [empLoading, setEmpLoading] = useState(false);

  useEffect(() => {
    api.get('/reports/staff-productivity').then(r => setProductivity(r.data));
    api.get('/reports/deadline-misses').then(r => setMisses(r.data));
    api.get('/clients').then(r => setClients(r.data));
  }, []);

  useEffect(() => {
    if (tab === 'employee') {
      setEmpLoading(true);
      Promise.all([
        api.get('/reports/employee-performance'),
        api.get('/reports/employee-comparison'),
      ]).then(([perf, cmp]) => {
        setEmpPerf(perf.data);
        setEmpComparison(cmp.data);
      }).finally(() => setEmpLoading(false));
    }
  }, [tab]);

  const loadClientReport = async id => {
    if (!id) return;
    const r = await api.get(`/reports/client-compliance/${id}`);
    setClientReport(r.data);
  };

  const exportEmpCSV = () => {
    const rows = [
      ['Name', 'Role', 'Total Tasks', 'Done', 'Overdue', 'Completion%', 'Compliance Filed', 'Clients Handled', 'Meetings Led', 'Revenue Billed'],
      ...empPerf.map(e => [e.name, e.role, e.totalTasks, e.doneTasks, e.overdueTasks, e.completionRate, e.compHandled, e.clientsHandled, e.meetingsLed, e.revenueBilled ?? 0]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const a = document.createElement('a'); a.href = 'data:text/csv,' + encodeURIComponent(csv); a.download = 'employee_performance.csv'; a.click();
  };

  const TABS = [
    { key: 'productivity', label: 'Staff Productivity', icon: Users },
    { key: 'misses', label: 'Deadline Misses', icon: AlertTriangle },
    { key: 'client', label: 'Client Report', icon: BarChart3 },
    { key: 'employee', label: 'Employee Performance', icon: Award },
  ];

  return (
    <div>
      <PageHeader title="Reports & MIS" subtitle="Analytics and compliance insights" />

      <div className="flex gap-1 mb-6">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === key ? 'bg-[#1e3a5f] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
            <Icon size={15} />{label}
          </button>
        ))}
      </div>

      {tab === 'productivity' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {productivity.map(u => (
            <div key={u.id} className="card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#1e3a5f] rounded-full flex items-center justify-center text-white font-bold">{u.name?.[0]}</div>
                <div>
                  <div className="font-semibold text-gray-800">{u.name}</div>
                  <div className="text-xs text-gray-400 capitalize">{u.role?.replace('_', ' ')}</div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Total Tasks</span><span className="font-semibold">{u.total}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Completed</span><span className="font-semibold text-green-600">{u.done}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">In Progress</span><span className="font-semibold text-blue-600">{u.inProgress}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Overdue</span><span className={`font-semibold ${u.overdue > 0 ? 'text-red-600' : 'text-gray-400'}`}>{u.overdue}</span></div>
              </div>
              {u.total > 0 && (
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Completion</span>
                    <span>{Math.round(u.done / u.total * 100)}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${u.total > 0 ? Math.round(u.done / u.total * 100) : 0}%` }} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'misses' && (
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2"><AlertTriangle size={18} className="text-orange-500" /> Late Filings & Overdue</h3>
          {misses.length === 0 ? <p className="text-gray-400 text-sm">No deadline misses. Great job!</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b"><th className="table-header">Module</th><th className="table-header">Description</th><th className="table-header">Client</th><th className="table-header">Due Date</th><th className="table-header">Filed On</th><th className="table-header">Days Late</th></tr></thead>
                <tbody>
                  {misses.map((m, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="table-cell"><span className={`text-xs px-2 py-0.5 rounded ${m.module === 'TDS Return' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>{m.module}</span></td>
                      <td className="table-cell font-medium">{m.description}</td>
                      <td className="table-cell">{m.client_name}</td>
                      <td className="table-cell">{fmtDate(m.due_date)}</td>
                      <td className="table-cell">{fmtDate(m.filing_date)}</td>
                      <td className="table-cell"><span className={`font-bold ${m.days_late > 30 ? 'text-red-600' : m.days_late > 0 ? 'text-orange-600' : 'text-red-600'}`}>{m.days_late > 0 ? `+${m.days_late} days` : `${Math.abs(m.days_late)} days pending`}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'employee' && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <input
              className="input-field w-52"
              placeholder="Search employee..."
              value={empFilter}
              onChange={e => setEmpFilter(e.target.value)}
            />
            <button onClick={exportEmpCSV} className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
              <Download size={14} /> Export CSV
            </button>
          </div>

          {empLoading ? (
            <div className="text-center py-16 text-gray-400">Loading...</div>
          ) : (
            <>
              {/* Individual cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {empPerf.filter(e => (!empFilter || e.name.toLowerCase().includes(empFilter.toLowerCase())) && (!empFilterId || e.id === empFilterId)).map(e => (
                  <div key={e.id} className="card space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-white text-sm flex-shrink-0" style={{ background: 'var(--btn-bg)' }}>{e.name?.[0]}</div>
                      <div>
                        <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{e.name}</div>
                        <div className="text-xs capitalize" style={{ color: 'var(--text-muted)' }}>{e.designation || e.role?.replace(/_/g,' ')}</div>
                        {e.date_of_joining && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Joined {fmtDate(e.date_of_joining)}</div>}
                      </div>
                      <div className="ml-auto text-right">
                        <div className="text-2xl font-bold" style={{ color: 'var(--sidebar-accent)' }}>{e.completionRate}%</div>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>completion</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="rounded-lg p-2" style={{ background: 'var(--main-bg)' }}>
                        <div className="font-bold text-base" style={{ color: 'var(--text-primary)' }}>{e.doneTasks}</div>
                        <div style={{ color: 'var(--text-muted)' }}>Done</div>
                      </div>
                      <div className="rounded-lg p-2" style={{ background: 'var(--main-bg)' }}>
                        <div className={`font-bold text-base ${e.overdueTasks > 0 ? 'text-red-500' : ''}`}>{e.overdueTasks}</div>
                        <div style={{ color: 'var(--text-muted)' }}>Overdue</div>
                      </div>
                      <div className="rounded-lg p-2" style={{ background: 'var(--main-bg)' }}>
                        <div className="font-bold text-base" style={{ color: 'var(--text-primary)' }}>{e.clientsHandled}</div>
                        <div style={{ color: 'var(--text-muted)' }}>Clients</div>
                      </div>
                    </div>

                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between" style={{ color: 'var(--text-secondary)' }}>
                        <span>Task Completion</span><span>{e.doneTasks}/{e.totalTasks}</span>
                      </div>
                      <MiniBar value={e.doneTasks} max={Math.max(e.totalTasks,1)} color="bg-green-500" />
                      {e.compHandled > 0 && <>
                        <div className="flex justify-between" style={{ color: 'var(--text-secondary)' }}>
                          <span>Compliance Filed</span><span>{e.compHandled}</span>
                        </div>
                        <MiniBar value={e.compHandled} max={Math.max(...empPerf.map(x=>x.compHandled),1)} color="bg-blue-500" />
                      </>}
                    </div>

                    {e.monthlyTrend?.length > 0 && (
                      <div>
                        <div className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>Tasks Done — Last 6 Months</div>
                        <MonthChart trend={e.monthlyTrend} />
                      </div>
                    )}

                    <div className="flex flex-wrap gap-3 text-xs pt-1 border-t" style={{ borderColor: 'var(--card-border)', color: 'var(--text-muted)' }}>
                      <span>Compliance: <strong style={{ color: 'var(--text-primary)' }}>{e.compHandled}</strong></span>
                      <span>Meetings: <strong style={{ color: 'var(--text-primary)' }}>{e.meetingsLed}</strong></span>
                      {e.onTimeRate != null && <span>On-time: <strong className="text-green-600">{e.onTimeRate}%</strong></span>}
                      <span>Revenue: <strong style={{ color: 'var(--text-primary)' }}>{fmtCurrency(e.revenueBilled || 0)}</strong></span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Comparison table */}
              {empComparison.length > 0 && (
                <div className="card">
                  <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <BarChart3 size={17} /> Team Comparison
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b" style={{ borderColor: 'var(--card-border)' }}>
                          {['Employee','Role','Total Tasks','Done','Overdue','Compliance Filed','Clients','Completion%'].map(h => (
                            <th key={h} className="text-left py-2 px-3 text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {empComparison.sort((a,b) => b.completionRate - a.completionRate).map((e, i) => (
                          <tr key={e.id} className="border-b" style={{ borderColor: 'var(--card-border)' }}>
                            <td className="py-2 px-3 font-medium" style={{ color: 'var(--text-primary)' }}>
                              {i === 0 && <span className="text-yellow-500 mr-1">🏆</span>}{e.name}
                            </td>
                            <td className="py-2 px-3 capitalize text-xs" style={{ color: 'var(--text-muted)' }}>{e.role?.replace(/_/g,' ')}</td>
                            <td className="py-2 px-3 text-center">{e.totalTasks}</td>
                            <td className="py-2 px-3 text-center text-green-600 font-medium">{e.doneTasks}</td>
                            <td className="py-2 px-3 text-center"><span className={e.overdueTasks > 0 ? 'text-red-500 font-semibold' : 'text-gray-400'}>{e.overdueTasks}</span></td>
                            <td className="py-2 px-3 text-center">{e.compFiled}</td>
                            <td className="py-2 px-3 text-center">{e.clientsHandled}</td>
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${e.completionRate >= 80 ? 'bg-green-500' : e.completionRate >= 50 ? 'bg-yellow-400' : 'bg-red-400'}`} style={{ width: `${e.completionRate}%` }} />
                                </div>
                                <span className="text-xs font-medium w-8">{e.completionRate}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'client' && (
        <div>
          <div className="card mb-4">
            <div className="flex gap-3">
              <select value={selectedClient} onChange={e => { setSelectedClient(e.target.value); loadClientReport(e.target.value); }} className="input-field w-64">
                <option value="">Select a client</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.client_code ? c.client_code + ' — ' + c.name : c.name}</option>)}
              </select>
            </div>
          </div>

          {clientReport && (
            <div className="space-y-4">
              <div className="card">
                <h3 className="font-bold text-[#1e3a5f] text-lg mb-2">{clientReport.client.name}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-gray-400">PAN:</span> <span className="font-mono font-medium">{clientReport.client.pan || '—'}</span></div>
                  <div><span className="text-gray-400">Entity:</span> <span>{clientReport.client.entity_type}</span></div>
                  <div><span className="text-gray-400">Status:</span> <StatusBadge status={clientReport.client.status} /></div>
                  <div><span className="text-gray-400">Services:</span> <span className="text-xs">{clientReport.client.services?.join(', ')}</span></div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {clientReport.itr?.length > 0 && (
                  <div className="card">
                    <h4 className="font-semibold text-gray-700 mb-3">ITR Filings</h4>
                    {clientReport.itr.map(i => (
                      <div key={i.id} className="flex justify-between text-sm py-1 border-b last:border-0">
                        <span>{i.ay} — {i.itr_form}</span><StatusBadge status={i.status} />
                      </div>
                    ))}
                  </div>
                )}
                {clientReport.gst?.length > 0 && (
                  <div className="card">
                    <h4 className="font-semibold text-gray-700 mb-3">Recent GST Returns</h4>
                    {clientReport.gst.slice(0, 6).map(g => (
                      <div key={g.id} className="flex justify-between text-sm py-1 border-b last:border-0">
                        <span>{g.return_type} {g.period}</span><StatusBadge status={g.status} />
                      </div>
                    ))}
                  </div>
                )}
                {clientReport.tasks?.length > 0 && (
                  <div className="card">
                    <h4 className="font-semibold text-gray-700 mb-3">Pending Tasks ({clientReport.tasks.length})</h4>
                    {clientReport.tasks.map(t => (
                      <div key={t.id} className="flex justify-between text-sm py-1 border-b last:border-0">
                        <span className="truncate">{t.title}</span><span className="text-xs text-gray-400">{fmtDate(t.due_date)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {clientReport.invoices?.length > 0 && (
                  <div className="card">
                    <h4 className="font-semibold text-gray-700 mb-3">Outstanding Invoices</h4>
                    {clientReport.invoices.map(i => (
                      <div key={i.id} className="flex justify-between text-sm py-1 border-b last:border-0">
                        <span>{i.invoice_number}</span><span className="text-red-600 font-semibold">{fmtCurrency(i.total - i.paid_amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
